/**
 * EVL-6 — `REQUIREMENT_TO_FIELDS` registry (config facade).
 *
 * Canonical registry lives in `src/lib/evidenceLineage/requirementToFields.ts`.
 * This module re-exports it and hosts config-adjacent helpers that depend on
 * `REQUIREMENT_TO_SECTION_MAP`.
 */
import type { SedSectionId, SiteEvidenceDocument } from '../types/siteEvidence';
import { REQUIREMENT_TO_SECTION_MAP } from './requirementToSection';

export {
  REQUIREMENT_TO_FIELDS,
  type FieldPath,
  type FieldProvenanceKind,
  type ResolvedField,
  INLINE_FIELD_BUDGET,
  extractFieldValue,
  resolveRequirementFields,
  resolveInlineFields,
  formatFieldValue,
} from '../lib/evidenceLineage/requirementToFields';

import { REQUIREMENT_TO_FIELDS } from '../lib/evidenceLineage/requirementToFields';

/**
 * Extract the SED section id (root) from a dot-path entry.
 */
export function sectionIdFromFieldPath(fieldPath: string): string {
  const idx = fieldPath.indexOf('.');
  return idx === -1 ? fieldPath : fieldPath.slice(0, idx);
}

/**
 * Extract the leaf field key (final dot-path segment).
 */
export function leafKeyFromFieldPath(fieldPath: string): string {
  const idx = fieldPath.lastIndexOf('.');
  return idx === -1 ? fieldPath : fieldPath.slice(idx + 1);
}

/** Return the SED field paths registered for a requirement. */
export function fieldsForRequirement(requirementId: string): ReadonlyArray<string> {
  return REQUIREMENT_TO_FIELDS[requirementId] ?? [];
}

/**
 * Resolve a checklist requirement id from a SED section + field key
 * (UFC / variable-browser focus shape: `parcel` + `acres`).
 * When multiple requirements share a path, prefer the section-primary owner.
 */
export function requirementIdForSedField(sectionId: string, fieldKey: string): string | null {
  const dotPath = `${sectionId}.${fieldKey}`;
  const variablePath = requirementFieldPathToVariablePath(dotPath);
  const matches: string[] = [];
  for (const [requirementId, paths] of Object.entries(REQUIREMENT_TO_FIELDS)) {
    for (const fp of paths) {
      if (fp === dotPath || fp === sectionId) {
        matches.push(requirementId);
        break;
      }
      if (requirementFieldPathToVariablePath(fp) === variablePath) {
        matches.push(requirementId);
        break;
      }
    }
  }
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;
  const pref = SECTION_REQUIREMENT_PREFERENCE[sectionId];
  if (pref) {
    for (const id of pref) {
      if (matches.includes(id)) return id;
    }
  }
  return matches[0]!;
}

/** Disambiguate shared SED paths (e.g. parcel.acres on geotech vs parcel-survey). */
const SECTION_REQUIREMENT_PREFERENCE: Partial<Record<string, readonly string[]>> = {
  parcel: ['parcel-survey', 'alta-survey', 'zoning', 'survey', 'utility-locate', 'geotech'],
  grid: ['utility-letter', 'fiber-survey'],
  flood: ['flood-cert'],
  seismic: ['geotech'],
  geotechnical: ['geotech'],
  survey: ['alta-survey', 'survey'],
  mineralResources: ['mineral-title'],
  culturalResources: ['archaeological-survey', 'cultural'],
  connectivity: ['fiber-survey'],
  waterResource: ['municipal-utility-letter', 'water-rights', 'discharge-permit'],
  publicSentiment: ['community-feedback-summary'],
};

/**
 * Resolve a BARE focus id (no dot) to the checklist requirement that owns it.
 * Path-to-GO next-steps deep-link with bare targetIds from three producers:
 * a checklist requirement id (coverage rows, e.g. 'geotech'), a SED section id
 * (preflight, e.g. 'climate'), or a provider/field id (sed-gaps). Order:
 *   1. the id IS a registered requirement → itself;
 *   2. treat it as a SED section id — the requirement whose registered paths
 *      live under that section, disambiguated by the same section-owner
 *      preference `requirementIdForSedField` uses;
 *   3. no match → null (the caller falls back to pane-level focus, as before).
 */
export function requirementIdForBareFocusId(id: string): string | null {
  if (!id || id.includes('.')) return null;
  if (REQUIREMENT_TO_FIELDS[id]) return id;
  const matches: string[] = [];
  for (const [requirementId, paths] of Object.entries(REQUIREMENT_TO_FIELDS)) {
    if (paths.some((fp) => fp === id || fp.startsWith(`${id}.`))) {
      matches.push(requirementId);
    }
  }
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;
  const pref = SECTION_REQUIREMENT_PREFERENCE[id];
  if (pref) {
    for (const reqId of pref) {
      if (matches.includes(reqId)) return reqId;
    }
  }
  return matches[0]!;
}

/**
 * Map a registry dot-path (`parcel.acres`) to the SED Variable Browser
 * path shape (`parcel.data.acres`).
 */
export function requirementFieldPathToVariablePath(fieldPath: string): string {
  if (fieldPath.includes('.data.')) return fieldPath;
  const dot = fieldPath.indexOf('.');
  if (dot === -1) return fieldPath;
  const section = fieldPath.slice(0, dot);
  const tail = fieldPath.slice(dot + 1);
  return `${section}.data.${tail}`;
}

/**
 * Build the variable-path scope for a requirement — used when deep-
 * linking from Coverage Matrix → Extracted Variables.
 */
export function variablePathsForRequirement(
  requirementId: string,
  sed?: SiteEvidenceDocument,
): Set<string> {
  const out = new Set<string>();
  for (const fp of fieldsForRequirement(requirementId)) {
    if (!fp.includes('.')) {
      if (fp === 'publicSentiment' && sed?.publicSentiment) {
        out.add('publicSentiment.perTech');
        out.add('publicSentiment.entries');
        continue;
      }
      const section = sed
        ? (sed as unknown as Record<string, { data?: Record<string, unknown> }>)[fp]
        : undefined;
      if (section?.data) {
        for (const key of Object.keys(section.data)) {
          out.add(`${fp}.data.${key}`);
        }
      }
      continue;
    }
    if (fp.startsWith('publicSentiment.')) {
      out.add(fp);
      continue;
    }
    out.add(requirementFieldPathToVariablePath(fp));
  }
  return out;
}

/** Exact registered roots admit their children, never similarly named siblings. Empty means no scope. */
export function variablePathInRequirementScope(path: string, scopedPaths: ReadonlySet<string>): boolean {
  for (const registered of scopedPaths) {
    if (path === registered || path.startsWith(registered + '.')) return true;
  }
  return false;
}

export interface FieldPathInconsistency {
  requirementId: string;
  fieldPath: string;
  missingSection: SedSectionId;
}

/** Pins field-registry section roots against REQUIREMENT_TO_SECTION_MAP. */
export function findFieldRegistryInconsistencies(): FieldPathInconsistency[] {
  const out: FieldPathInconsistency[] = [];
  for (const [requirementId, fields] of Object.entries(REQUIREMENT_TO_FIELDS)) {
    const sections = new Set<string>(REQUIREMENT_TO_SECTION_MAP[requirementId] ?? []);
    for (const fp of fields) {
      const sid = sectionIdFromFieldPath(fp);
      if (!sections.has(sid)) {
        out.push({ requirementId, fieldPath: fp, missingSection: sid as SedSectionId });
      }
    }
  }
  return out;
}
